import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/content';
import { useNamespaceCopy } from '@/i18n/ns';
import appLifecycleCopy from '@/i18n/ns/app-lifecycle';
import appOperationsCopy from '@/i18n/ns/app-operations';
import './lifecycle.css';

/** A modal over the app page, on the platform's `<dialog>`: focus is trapped and Esc closes it. */
export function AppDialog({
  title,
  description,
  closeLabel,
  busy = false,
  onClose,
  footer,
  children,
  testId,
}: {
  title: string;
  description?: string;
  closeLabel: string;
  /** While a write is in flight the dialog cannot be dismissed. */
  busy?: boolean;
  onClose: () => void;
  footer: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const node = dialog.current;
    if (!node || node.open) return;
    if (typeof node.showModal === 'function') node.showModal();
    else node.setAttribute('open', '');
    // Not the Close button: the control the person is meant to use (`data-autofocus`), else the title.
    (
      node.querySelector<HTMLElement>('[data-autofocus]:not(:disabled)') ??
      node.querySelector<HTMLElement>('h2')
    )?.focus();
    return () => {
      if (typeof node.close === 'function' && node.open) node.close();
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      className="pc-app-dialog"
      aria-labelledby={titleId}
      data-testid={testId}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      onMouseDown={(event) => {
        if (!busy && event.target === event.currentTarget) onClose();
      }}
    >
      <div className="pc-app-dialog-sheet">
        <header>
          <div>
            <h2 id={titleId} tabIndex={-1}>
              {title}
            </h2>
            {description && <p>{description}</p>}
          </div>
          <button
            type="button"
            className="pc-app-dialog-close"
            aria-label={closeLabel}
            disabled={busy}
            onClick={() => onClose()}
          >
            ×
          </button>
        </header>
        <div className="pc-app-dialog-body">{children}</div>
        <footer>{footer}</footer>
      </div>
    </dialog>
  );
}

/** What a lifecycle write reported: `refused` (the server confirmed nothing changed) or `uncertain` (unknown). */
export type LifecycleFailure = 'refused' | 'uncertain' | null;

/**
 * The unknown-outcome notice with an optional Check. Check is the host's original-only lookup (no
 * arguments, no retarget); without it the notice stands alone. It runs once at a time, never while the
 * dialog is busy, and a failed or still-unknown answer changes nothing here: the host decides.
 */
export function OutcomeUnknown({
  message,
  busy,
  onCheck,
  testId,
}: {
  message: string;
  busy: boolean;
  onCheck?: () => void | Promise<void>;
  testId: string;
}) {
  const lifecycle = useNamespaceCopy(appLifecycleCopy);
  const operations = useNamespaceCopy(appOperationsCopy);
  const [checking, setChecking] = useState(false);
  const running = useRef(false);
  const check = async () => {
    if (running.current || busy || !onCheck) return;
    running.current = true;
    setChecking(true);
    try {
      await onCheck();
    } catch {
      /* the host's failure state is the truth; a failed lookup proves nothing */
    } finally {
      running.current = false;
      setChecking(false);
    }
  };
  return (
    <div data-testid={testId}>
      <Callout tone="warning">
        {message}{' '}
        {onCheck && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid={`${testId}-check`}
            disabled={busy || checking}
            onClick={() => void check()}
          >
            {checking ? lifecycle.checkingOutcome : operations.checkAgain}
          </Button>
        )}
        {!onCheck && <small data-testid={`${testId}-reload`}>{lifecycle.unknownNoCheckHint}</small>}
      </Callout>
      <small data-testid={`${testId}-hint`}>{lifecycle.unknownBlockedHint}</small>
    </div>
  );
}
